import { getCountryName, loadCountriesLocale } from '@internal/utils/countries'
import { localeNamespace, TLocale } from '@internal/utils/machine/i18n/Locale'
import {
  baleChamberTranslationMapping,
  balerSubtypeTranslationMapping,
  machineHeaderTypeTranslationMapping,
} from '@internal/utils/machine/i18n/translationMappings'
import { MachineAttribute } from '@internal/utils/machine/MachineAttribute'
import { getMachineUnit } from '@internal/utils/machine/machineUnits'
import { useMachineTypeName } from '@internal/utils/machine/useMachineTypeName'
import { useModelLabel } from '@internal/utils/machine/useModelLabel'
import { Chip, Typography } from '@mui/material'
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery'
import { useSearchParams } from 'blocks/SearchResultsBlock/useSearchParams'
import { translations } from 'i18n/translations'
import { omitBy } from 'lodash'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styles from './filterChips.module.scss'
import { TFiltersConfiguration } from './TFiltersConfiguration'
import { TRangeFilterValues } from './TRangeFilterValues'
import { SearchModel, TSearchParams } from './TSearchParams'
import { useSearchResultsRouting } from './useSearchResultsRouting'
import { useTranslationsFromFiltersConfiguration } from './useTranslationsFromFiltersConfiguration'

type FilterType = keyof Pick<
  TSearchParams,
  | 'machineType'
  | 'brands'
  | 'models'
  | 'features'
  | 'rangeFilters'
  | 'locations'
  | 'machineSubType'
  | 'baleChamber'
  | 'headerType'
>

type Filter =
  | {
      type: FilterType
      label: string
      value: string
    }
  | {
      type: 'models'
      label: string
      value: SearchModel
    }

const getRangeFilterLabel = (
  label: string,
  values: TRangeFilterValues,
  unit?: string
) => {
  if (values.min === undefined) {
    return `${label}: <= ${values.max}`
  } else if (values.max === undefined) {
    return `${label}: >= ${values.min}`
  }
  return `${label ? `${label}: ` : ''}${values.min} - ${values.max} ${unit ?? ''}`.trim()
}

const ClickableChip: FC<{
  filter: Filter
  onDeleteClick: (filter: Filter) => void
}> = ({ filter, onDeleteClick }) => {
  const handleDelete = useCallback(() => {
    onDeleteClick(filter)
  }, [filter, onDeleteClick])

  return (
    <Chip
      label={filter.label}
      component='a'
      variant='outlined'
      onDelete={handleDelete}
    />
  )
}

export const FilterChips: React.FC<{
  filtersConfiguration: TFiltersConfiguration
  locale: TLocale
}> = ({ filtersConfiguration, locale }) => {
  const { t } = useTranslation(localeNamespace.common)
  const { getMachineTypeName } = useMachineTypeName()
  const { gotoSearchResults } = useSearchResultsRouting()
  const { isMobile } = useMediaQuery()
  const searchParams = useSearchParams()
  const [showAll, setShowAll] = useState(false)
  const { getModelLabel } = useModelLabel()
  const { getTranslation } =
    useTranslationsFromFiltersConfiguration(filtersConfiguration)
  const [countryLocalesReady, setCountryLocalesReady] = useState(false)

  useEffect(() => {
    setCountryLocalesReady(false)
    void loadCountriesLocale(locale)
      .then(() => setCountryLocalesReady(true))
      .catch(() => setCountryLocalesReady(true))
  }, [locale])

  const allFilters: Filter[] = useMemo(() => {
    if (!countryLocalesReady) {
      return []
    }

    const machineTypeFilter: Filter[] = searchParams.machineType
      ? [
          {
            type: 'machineType',
            label: getMachineTypeName(searchParams.machineType, 'plural'),
            value: searchParams.machineType,
          },
        ]
      : []
    const brandsFilter: Filter[] = searchParams.brands.map((brand) => ({
      type: 'brands',
      label: brand,
      value: brand,
    }))
    const modelFilters: Filter[] = searchParams.models
      ? searchParams.models.map((model) => ({
          type: 'models',
          label: getModelLabel(model.name, model.type),
          value: model,
        }))
      : []
    const machineSubTypeFilter: Filter[] = searchParams.machineSubType
      ? [
          {
            type: 'machineSubType',
            label: t(
              balerSubtypeTranslationMapping[searchParams.machineSubType]
            ),
            value: searchParams.machineSubType,
          },
        ]
      : []
    const baleChamberFilter: Filter[] = searchParams.baleChamber
      ? [
          {
            type: 'baleChamber',
            label: t(baleChamberTranslationMapping[searchParams.baleChamber]),
            value: searchParams.baleChamber,
          },
        ]
      : []
    const headerTypeFilter: Filter[] = searchParams.headerType
      ? [
          {
            type: 'headerType',
            label: t(
              machineHeaderTypeTranslationMapping[searchParams.headerType]
            ),
            value: searchParams.headerType,
          },
        ]
      : []
    const rangeFilters: Filter[] = Object.entries(
      searchParams.rangeFilters
    ).map(([attribute, values]) => ({
      type: 'rangeFilters',
      label: getRangeFilterLabel(
        getTranslation(attribute),
        values,
        getMachineUnit(attribute as MachineAttribute, locale)
      ),
      value: attribute,
    }))
    const featuresFilter: Filter[] = searchParams.features.map((feature) => ({
      type: 'features',
      label: getTranslation(feature),
      value: feature,
    }))
    const locationsFilter: Filter[] = searchParams.locations.map(
      (location) => ({
        type: 'locations',
        label: getCountryName(locale, location) ?? location,
        value: location,
      })
    )
    return [
      ...machineTypeFilter,
      ...brandsFilter,
      ...modelFilters,
      ...machineSubTypeFilter,
      ...baleChamberFilter,
      ...headerTypeFilter,
      ...rangeFilters,
      ...featuresFilter,
      ...locationsFilter,
    ]
  }, [
    countryLocalesReady,
    searchParams,
    getMachineTypeName,
    t,
    getModelLabel,
    getTranslation,
    locale,
  ])

  const handleDeleteClick = useCallback(
    (filter: Filter) => {
      switch (filter.type) {
        case 'machineType':
        case 'machineSubType':
        case 'baleChamber':
        case 'headerType':
          gotoSearchResults(
            {
              ...searchParams,
              [filter.type]: undefined,
            },
            isMobile
          )
          break
        case 'models':
          const filterModel =
            typeof filter.value === 'string' ? null : filter.value
          if (!filterModel) {
            return
          }

          gotoSearchResults(
            {
              ...searchParams,
              models: searchParams.models.filter(
                (model) =>
                  model.type !== filterModel.type ||
                  model.name !== filterModel.name
              ),
            },
            isMobile
          )
          break
        case 'brands':
        case 'features':
        case 'locations':
          gotoSearchResults(
            {
              ...searchParams,
              [filter.type]: searchParams[filter.type].filter(
                (f) => f !== filter.value
              ),
            },
            isMobile
          )
          break
        case 'rangeFilters':
          gotoSearchResults({
            ...searchParams,
            rangeFilters: omitBy(
              searchParams.rangeFilters,
              (_, key) => key === filter.value
            ),
          })
          break
      }
    },
    [gotoSearchResults, isMobile, searchParams]
  )

  const handleShowAll = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault()
      setShowAll((prev) => !prev)
    },
    []
  )

  const visibleFilters = useMemo(
    () => (showAll ? allFilters : allFilters.slice(0, 10)),
    [showAll, allFilters]
  )
  const amountOfHiddenFilters = allFilters.length - visibleFilters.length

  if (visibleFilters.length === 0) {
    return null
  }

  return (
    <div className={styles.wrapper}>
      {visibleFilters.map((filter, index) => (
        <ClickableChip
          key={`${filter.type}-${filter.value}-${index}`}
          filter={filter}
          onDeleteClick={handleDeleteClick}
        />
      ))}
      {amountOfHiddenFilters > 0 && (
        <Link
          className={styles.showMoreButton}
          href='#'
          onClick={handleShowAll}
        >
          <Typography variant='caption' color='text.primaryLight'>
            {showAll && t(translations.searchResultsChipsShowLess)}
            {!showAll &&
              t(translations.searchResultsChipsShowMore, {
                amount: amountOfHiddenFilters,
              })}
          </Typography>
        </Link>
      )}
    </div>
  )
}
